import { InputCell, Hint, InputsRow } from 'components/cooperation-form/styled';
import { PhoneNumber, Select, TextField, AutoComplete } from 'kl-b2c-ui-kit';
import React, { FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CourseRegistration } from 'types';
import {
    CourseFormType,
    EventType,
    StudyCourse,
    StudyLevel,
} from 'types/enums';
import { DefaultTheme, withTheme } from 'styled-components';
import { CourseRegistrationUI, getCourseCooperationFormRules } from './configs';
import { useCourse } from 'contexts/course.context';

const INPUT_CELL_STYLES = { width: '100%', margin: '0 0 8px 0' };
export const DEFAULT_FORM_STATE: CourseRegistrationUI = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    company: '',
    position: '',
    countOfPeople: undefined,
    studyCourse: undefined,
    studyLevel: undefined,
    studyForm: undefined,
};

interface RegistrationFormRowsProps {
    type: CourseFormType;
    theme: DefaultTheme;
    withoutUniversity?: boolean;
}

const RegistrationFormRows: FC<RegistrationFormRowsProps> = ({
    type,
    withoutUniversity,
}) => {
    const {
        control,
        formState: { errors },
        reset,
        setValue,
        clearErrors,
        watch,
    } = useFormContext<CourseRegistration>();

    const locale = process.env.REACT_APP_LOCALE as 'ru' | 'en';

    const { getUniversities } = useCourse();

    const studyLevelWatch = watch('studyLevel')?.valueOf();
    const universityInner = watch('universityInnerId')?.valueOf();

    const { t } = useTranslation([
        'pages/courses',
        'common/shared',
        'common/errors',
    ]);

    const selectStyles = {
        container: {
            height: '46px',
        },
        placeholder: {
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '16px',
        },
    };

    useEffect(() => {
        reset(DEFAULT_FORM_STATE);
        clearErrors();
    }, [type]);

    const rules = getCourseCooperationFormRules(t);

    return (
        <>
            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'firstName'}
                    control={control}
                    rules={rules['firstName']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:firstName')}
                            error={!!errors.firstName}
                            atSelector={'courseRegistrationForm_firstName'}
                        />
                    )}
                />
                {errors.firstName && <Hint>{errors.firstName.message}</Hint>}
            </InputCell>
            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'lastName'}
                    control={control}
                    rules={rules['lastName']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:lastName')}
                            error={!!errors.lastName}
                            atSelector={'courseRegistrationForm_lastName'}
                        />
                    )}
                />
                {errors.lastName && <Hint>{errors.lastName.message}</Hint>}
            </InputCell>
            <InputsRow styles={{ gap: '8px', margin: '0 0 8px 0' }}>
                <InputCell
                    styles={
                        type === CourseFormType.XTraining
                            ? { width: '100%' }
                            : {}
                    }>
                    <Controller
                        name={'email'}
                        control={control}
                        rules={rules['email']}
                        render={({ field: { onChange } }) => (
                            <TextField
                                fullWidth
                                variant={'border'}
                                type={'text'}
                                onChange={onChange}
                                placeholder={t('pages/courses:email')}
                                error={!!errors.email}
                                atSelector={'courseRegistrationForm_email'}
                            />
                        )}
                    />
                    {errors.email && <Hint>{errors.email.message}</Hint>}
                </InputCell>
                {type !== CourseFormType.XTraining && (
                    <InputCell>
                        <Controller
                            name={'phone'}
                            control={control}
                            rules={rules['phone']}
                            render={({ field: { onChange, value } }) => (
                                <PhoneNumber
                                    value={value}
                                    variant={'border'}
                                    placeholder={t('pages/courses:phone')}
                                    error={!!errors.phone}
                                    onChange={onChange}
                                    fullWidth
                                    atSelector={'courseRegistrationForm_phone'}
                                />
                            )}
                        />
                        {errors.phone && <Hint>{errors.phone.message}</Hint>}
                    </InputCell>
                )}
            </InputsRow>
            <InputCell styles={INPUT_CELL_STYLES}>
                <Controller
                    name={'country'}
                    control={control}
                    rules={rules['country']}
                    render={({ field: { onChange } }) => (
                        <TextField
                            fullWidth
                            variant={'border'}
                            type={'text'}
                            onChange={onChange}
                            placeholder={t('pages/courses:country')}
                            error={!!errors.country}
                            atSelector={'courseRegistrationForm_country'}
                        />
                    )}
                />
                {errors.country && <Hint>{errors.country.message}</Hint>}
            </InputCell>
            {type !== CourseFormType.Individual &&
                type !== CourseFormType.Student && (
                    <InputCell styles={INPUT_CELL_STYLES}>
                        <Controller
                            name={'company'}
                            control={control}
                            rules={rules['company']}
                            render={({ field: { onChange } }) => (
                                <TextField
                                    fullWidth
                                    variant={'border'}
                                    type={'text'}
                                    onChange={onChange}
                                    placeholder={
                                        type === CourseFormType.Teacher
                                            ? t('pages/courses:university')
                                            : t('pages/courses:company')
                                    }
                                    error={!!errors.company}
                                    atSelector={
                                        type === CourseFormType.Teacher
                                            ? 'courseRegistrationForm_university'
                                            : 'courseRegistrationForm_company'
                                    }
                                />
                            )}
                        />
                        {errors.company && (
                            <Hint>{errors.company.message}</Hint>
                        )}
                    </InputCell>
                )}
            {type === CourseFormType.Student && (
                <>
                    {locale === 'ru' && !withoutUniversity && (
                        <InputCell styles={INPUT_CELL_STYLES}>
                            <Controller
                                name={'universityInnerId'}
                                control={control}
                                rules={rules['universityInnerId']}
                                render={({ field: { onChange } }) => (
                                    <AutoComplete
                                        getItems={(val) =>
                                            getUniversities({
                                                size: 100,
                                                page: 0,
                                                name: val,
                                            }).then((d) => [
                                                ...d.data.items.map((m) => ({
                                                    key: m.name,
                                                    value: m.id,
                                                })),
                                                {
                                                    key: t(
                                                        'pages/courses:other'
                                                    ),
                                                    value: '111',
                                                },
                                            ])
                                        }
                                        fullWidth
                                        variant={'border'}
                                        type={'text'}
                                        onChoose={(v) => {
                                            onChange(v?.value);
                                            setValue('university', undefined);
                                        }}
                                        placeholder={t(
                                            'pages/courses:universityInnerId'
                                        )}
                                        error={!!errors.universityInnerId}
                                        iconColor={'black'}
                                        styles={{
                                            iconContainer: {
                                                top: '-5px',
                                            },
                                            option: {
                                                fontSize: '14px',
                                                lineHeight: '1',
                                                color: 'rgba(0,0,0, 0.7)',
                                                cursor: 'pointer',
                                            },
                                        }}
                                        atSelector={{
                                            container:
                                                'courseRegistrationForm_studentUniversity',
                                            optionItem:
                                                'courseRegistrationForm_studentUniversity_item',
                                            optionsContainer:
                                                'courseRegistrationForm_studentUniversity_optionsContainer',
                                        }}
                                    />
                                )}
                            />
                            {errors.universityInnerId && (
                                <Hint>{errors.universityInnerId.message}</Hint>
                            )}
                        </InputCell>
                    )}
                    {((universityInner === '111' && locale === 'ru') ||
                        locale === 'en' ||
                        withoutUniversity) && (
                        <InputCell styles={INPUT_CELL_STYLES}>
                            <Controller
                                name={'university'}
                                control={control}
                                rules={rules['university']}
                                render={({ field: { onChange } }) => (
                                    <TextField
                                        fullWidth
                                        variant={'border'}
                                        type={'text'}
                                        onChange={onChange}
                                        placeholder={t(
                                            'pages/courses:university'
                                        )}
                                        error={!!errors.university}
                                        atSelector={
                                            'courseRegistrationForm_university'
                                        }
                                    />
                                )}
                            />
                            {errors.university && (
                                <Hint>{errors.university.message}</Hint>
                            )}
                        </InputCell>
                    )}
                    <InputCell styles={INPUT_CELL_STYLES}>
                        <Controller
                            name={'studyLevel'}
                            control={control}
                            rules={rules['studyLevel']}
                            render={({ field: { onChange } }) => (
                                <Select
                                    error={!!errors?.studyLevel}
                                    styles={selectStyles}
                                    onChoose={(
                                        item:
                                            | {
                                                  key: string;
                                                  value: string | number;
                                              }[]
                                            | undefined
                                    ) => {
                                        if (item && item.length) {
                                            onChange(
                                                item[0].value as StudyLevel
                                            );
                                        }
                                    }}
                                    placeholder={t('pages/courses:studyLevel')}
                                    variant={'border'}
                                    items={Object.keys(StudyLevel)
                                        .filter((el) => isNaN(Number(el)))
                                        .map((key) => ({
                                            key: t(
                                                `common/shared:study-level-${key}`
                                            ),
                                            value: StudyLevel[
                                                key as keyof typeof StudyLevel
                                            ],
                                        }))}
                                    atSelector={{
                                        container:
                                            'courseRegistrationForm_studyLevel',
                                        optionItem:
                                            'courseRegistrationForm_studyLevel_item',
                                        optionsContainer:
                                            'courseRegistrationForm_studyLevel_optionsContainer',
                                    }}
                                />
                            )}
                        />
                        {errors.studyLevel && (
                            <Hint>{errors.studyLevel.message}</Hint>
                        )}
                    </InputCell>
                    {studyLevelWatch &&
                        studyLevelWatch !== StudyLevel.Postgraduate && (
                            <InputCell styles={INPUT_CELL_STYLES}>
                                <Controller
                                    name={'studyCourse'}
                                    control={control}
                                    rules={rules['studyCourse']}
                                    render={({ field: { onChange } }) => (
                                        <Select
                                            error={!!errors?.studyCourse}
                                            styles={selectStyles}
                                            onChoose={(
                                                item:
                                                    | {
                                                          key: string;
                                                          value:
                                                              | string
                                                              | number;
                                                      }[]
                                                    | undefined
                                            ) => {
                                                if (item && item.length) {
                                                    onChange(
                                                        item[0]
                                                            .value as StudyCourse
                                                    );
                                                }
                                            }}
                                            placeholder={t(
                                                'pages/courses:studyCourse'
                                            )}
                                            variant={'border'}
                                            items={Object.keys(StudyCourse)
                                                .filter((el) => {
                                                    const v = Number(el);
                                                    const ifIsNan = isNaN(v);
                                                    if (!ifIsNan) return false;

                                                    const val =
                                                        StudyCourse[
                                                            el as keyof typeof StudyCourse
                                                        ];
                                                    return (
                                                        (studyLevelWatch ===
                                                            StudyLevel.Bachelor &&
                                                            val < 6) ||
                                                        studyLevelWatch ===
                                                            StudyLevel.Specialty ||
                                                        (studyLevelWatch ===
                                                            StudyLevel.Magistracy &&
                                                            val < 4) ||
                                                        (studyLevelWatch ===
                                                            StudyLevel.SecondaryProfessionalEducation &&
                                                            val < 5)
                                                    );
                                                })
                                                .map((key) => ({
                                                    key: t(
                                                        `common/shared:study-course-${key}`
                                                    ),
                                                    value: StudyCourse[
                                                        key as keyof typeof StudyCourse
                                                    ],
                                                }))}
                                            atSelector={{
                                                container:
                                                    'courseRegistrationForm_studyCourse',
                                                optionItem:
                                                    'courseRegistrationForm_studyCourse_item',
                                                optionsContainer:
                                                    'courseRegistrationForm_studyCourse_optionsContainer',
                                            }}
                                        />
                                    )}
                                />
                                {errors.studyCourse && (
                                    <Hint>{errors.studyCourse.message}</Hint>
                                )}
                            </InputCell>
                        )}
                </>
            )}
            {(type === CourseFormType.Teacher ||
                type === CourseFormType.Company) && (
                <InputCell styles={INPUT_CELL_STYLES}>
                    <Controller
                        name={'position'}
                        control={control}
                        rules={rules['position']}
                        render={({ field: { onChange } }) => (
                            <TextField
                                fullWidth
                                variant={'border'}
                                type={'text'}
                                onChange={onChange}
                                placeholder={t('pages/courses:position')}
                                error={!!errors.position}
                                atSelector={'courseRegistrationForm_position'}
                            />
                        )}
                    />
                    {errors.position && <Hint>{errors.position.message}</Hint>}
                </InputCell>
            )}
            {type !== CourseFormType.Individual &&
                type !== CourseFormType.Student && (
                    <InputCell styles={INPUT_CELL_STYLES}>
                        <Controller
                            name={'countOfPeople'}
                            control={control}
                            rules={rules['countOfPeople']}
                            render={({ field: { onChange } }) => (
                                <TextField
                                    fullWidth
                                    variant={'border'}
                                    type={'text'}
                                    onChange={onChange}
                                    placeholder={t(
                                        'pages/courses:countOfPeople'
                                    )}
                                    error={!!errors.countOfPeople}
                                    atSelector={
                                        'courseRegistrationForm_countOfPeople'
                                    }
                                />
                            )}
                        />
                        {errors.countOfPeople && (
                            <Hint>{errors.countOfPeople.message}</Hint>
                        )}
                    </InputCell>
                )}
            {type === CourseFormType.Company && (
                <InputCell styles={INPUT_CELL_STYLES}>
                    <Controller
                        name={'studyForm'}
                        control={control}
                        rules={rules['studyForm']}
                        render={({ field: { onChange } }) => (
                            <Select
                                error={!!errors?.studyForm}
                                styles={selectStyles}
                                onChoose={(
                                    item:
                                        | {
                                              key: string;
                                              value: string | number;
                                          }[]
                                        | undefined
                                ) => {
                                    if (item && item.length) {
                                        onChange(item[0].value as EventType);
                                    }
                                }}
                                placeholder={t('pages/courses:studyForm')}
                                variant={'border'}
                                items={Object.keys(EventType)
                                    .filter((el) => isNaN(Number(el)))
                                    .map((key) => ({
                                        key,
                                        value: EventType[
                                            key as keyof typeof EventType
                                        ],
                                    }))}
                                atSelector={{
                                    container:
                                        'courseRegistrationForm_studyForm',
                                    optionItem:
                                        'courseRegistrationForm_studyForm_item',
                                    optionsContainer:
                                        'courseRegistrationForm_studyForm_optionsContainer',
                                }}
                            />
                        )}
                    />
                    {errors.studyForm && (
                        <Hint>{errors.studyForm.message}</Hint>
                    )}
                </InputCell>
            )}
            {type === CourseFormType.XTraining && (
                <InputCell styles={INPUT_CELL_STYLES}>
                    <Controller
                        name={'comment'}
                        control={control}
                        rules={rules['comment']}
                        render={({ field: { onChange } }) => (
                            <TextField
                                isTextArea
                                fullWidth
                                variant={'border'}
                                type={'text'}
                                onChange={onChange}
                                placeholder={t('pages/courses:comment')}
                                error={!!errors.comment}
                                atSelector={'courseRegistrationForm_comment'}
                            />
                        )}
                    />
                    {errors.comment && <Hint>{errors.comment.message}</Hint>}
                </InputCell>
            )}
        </>
    );
};

export default withTheme(RegistrationFormRows);
